import React, { useState } from 'react';
import { Box, Container, Paper, Typography, TextField, Button, Grid } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useParams } from 'react-router-dom';

function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        phoneNumber: '',
        email: '',
        pinCode: '',
        instagramLink: '',
        youtubeLink: '',
        market: '',
        state: '',
        remarks: ''
    });
    const {id} = useParams();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showThankYou, setShowThankYou] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: '' });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.phoneNumber) {
            newErrors.phoneNumber = 'Phone Number is required';
        } else if (!/^[0-9]{10}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Phone Number must be 10 digits';
        }
        if (!formData.email) newErrors.email = 'Email is required';
        if (!formData.pinCode) {
            newErrors.pinCode = 'Pin Code is required';
        } else if (!/^[0-9]{6}$/.test(formData.pinCode)) {
            newErrors.pinCode = 'Pin Code must be 6 digits';
        }
        if (!formData.instagramLink) {
            newErrors.instagramLink = 'Instagram Profile Link is required';
        } else if (!formData.instagramLink.startsWith('https://')) {
            newErrors.instagramLink = 'Instagram link must start with https://';
        }
        if (!formData.youtubeLink) {
            newErrors.youtubeLink = 'YouTube Profile Link is required';
        } else if (!formData.youtubeLink.startsWith('https://')) {
            newErrors.youtubeLink = 'YouTube link must start with https://';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        try {
            await api.post('/contacts', { ...formData, campaignId: id });

            if (window.fbq) {
                window.fbq('track', 'Lead');
                console.log("Facebook Lead Event Tracked");
            }

            setShowThankYou(true);
            setFormData({
                name: '',
                username: '',
                phoneNumber: '',
                email: '',
                pinCode: '',
                instagramLink: '',
                youtubeLink: '',
                market: '',
                state: '',
                remarks: ''
            });
            setErrors({});
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to submit form';
            
            if (errorMessage === 'Instagram link already exists') {
                setErrors({ instagramLink: 'This Instagram link is already registered' });
                toast.error('Instagram link already exists');
            } else if (errorMessage === 'Youtube link already exists') {
                setErrors({ youtubeLink: 'This YouTube link is already registered' });
                toast.error('YouTube link already exists');
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleThankYouClose = () => {
        setShowThankYou(false);
    };

    if (showThankYou) {
        const ThankYou = React.lazy(() => import('./ThankYou'));
        return (
            <React.Suspense fallback={<div>Loading...</div>}>
                <ThankYou onClose={handleThankYouClose} />
            </React.Suspense>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#edf4fd', py: 4 }}>
            <Container maxWidth="md">
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: 'white' }}>
                    <Box sx={{ bgcolor: '#5386e4', color: 'white', p: 4, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            Cavin Kare - Information Form
                        </Typography>
                    </Box>
                    <Box sx={{ p: 5 }}>
                        <Typography variant="h6" sx={{ mb: 3, color: '#5386e4' }}>Basic Information</Typography>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Username" name="username" value={formData.username} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} error={!!errors.phoneNumber} helperText={errors.phoneNumber} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Instagram Profile Link" name="instagramLink" value={formData.instagramLink} onChange={handleChange} error={!!errors.instagramLink} helperText={errors.instagramLink} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="YouTube Profile Link" name="youtubeLink" value={formData.youtubeLink} onChange={handleChange} error={!!errors.youtubeLink} helperText={errors.youtubeLink} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="Pin Code" name="pinCode" value={formData.pinCode} onChange={handleChange} error={!!errors.pinCode} helperText={errors.pinCode} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="Market" name="market" value={formData.market} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="State" name="state" value={formData.state} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Remarks" name="remarks" multiline rows={4} value={formData.remarks} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ py: 1.5, bgcolor: '#5386e4' }}>
                                        {loading ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                </Paper>
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                        Powered by HEPL
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default ContactForm;
